import { MODELS } from '../data/models';
import { PROVIDERS } from '../data/providers';
import { state } from './state';
import { t } from './i18n';
import { escapeHtml, renderMarkdown, fmtUsd, slugify, nowReadable } from './ui-helpers';
import { toast } from './toast';
import { addSpent, calcCost } from './cost-tracking';
import { callModel } from '../api';
import { getConstellation } from './constellation';
import { renderSelectedPills, updatePickerMeta } from './selected-pills';
import { renderCostLegend } from './cost-legend';
import { mountExportMenu, buildResponseMarkdown } from './exports';
import { createInteraction } from './history-client';
import { triggerAutosave } from './autosave';
import { getAttachments } from './attachments';
import { extractCodeBlocks } from './code-block-extractor';
import { getCurrentLang } from './i18n';

/* =================================================================
   10. Run & render responses
   ================================================================= */
let runBtn = null;
let promptInput = null;
let charCount = null;
let responsesPanel = null;
let responsesGrid = null;
let responsesMeta = null;

export function updateRunButton() {
  if (!runBtn) runBtn = document.getElementById("run-btn");
  if (!promptInput) promptInput = document.getElementById("prompt-input");
  const ok = promptInput.value.trim().length > 0 && state.selectedModels.size > 0;
  runBtn.disabled = !ok;
}

function buildErrorBox(out) {
  const box = document.createElement("div");
  box.className = "response-error";
  const provider = out.provider || "Provider";
  const status = out.status ? `HTTP ${out.status}` : "";
  let html = `
    <div class="err-head">
      <span class="err-tag">${escapeHtml(provider)}${status ? " · " + status : ""}</span>
    </div>
    <div class="err-msg">${escapeHtml(out.err || "Unknown error")}</div>
  `;
  if (out.action) {
    html += `<a class="err-action" href="${out.action}" target="_blank" rel="noopener noreferrer">${t("err.fix_link")} →</a>`;
  }
  if (out.raw && out.raw !== out.err) {
    html += `<details class="err-raw"><summary>${t("err.show_raw")}</summary><pre>${escapeHtml(out.raw)}</pre></details>`;
  }
  box.innerHTML = html;
  return box;
}

/* -------- Card builders (shared between runQuery and runSingle) -------- */
function buildResponseCard(model) {
  const card = document.createElement("div");
  card.className = "response-card";
  card.dataset.cardModel = model.id;
  card.innerHTML = `
    <div class="response-head">
      <div class="left">
        <span class="provider-dot ${PROVIDERS[model.provider].colorClass}"></span>
        <span class="name">${escapeHtml(model.label)}</span>
      </div>
      <div class="meta" data-meta></div>
      <div class="response-actions">
        <button class="run-btn" data-run-model="${model.id}" title="${t("run.this")}">
          <svg viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 2.5l9 5.5-9 5.5z"/></svg>
        </button>
        <button class="save-btn" data-save-model="${model.id}" title="${t("save.this")}" disabled>
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 1v10M4 7l4 4 4-4M2 14h12"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="response-body" data-body></div>
  `;
  card.querySelector(".run-btn").addEventListener("click", () => runSingle(model.id));
  return card;
}

function setCardLoading(card) {
  card.classList.remove("ok", "err", "pending");
  card.querySelectorAll(".response-error").forEach(e => e.remove());
  const meta = card.querySelector("[data-meta]");
  const body = card.querySelector("[data-body]");
  meta.innerHTML = `<span class="live-pulse"></span>`;
  body.className = "response-body";
  body.innerHTML = `
    <div class="skeleton">
      <div class="skeleton-line"></div>
      <div class="skeleton-line"></div>
      <div class="skeleton-line"></div>
      <div class="skeleton-line"></div>
    </div>
  `;
  // Disable buttons while loading
  const runBtnEl = card.querySelector(".run-btn");
  const saveBtn = card.querySelector(".save-btn");
  if (runBtnEl) runBtnEl.disabled = true;
  if (saveBtn) saveBtn.disabled = true;
}

function applyCardResult(card, model, out) {
  card.classList.remove("pending");
  card.querySelectorAll(".response-error").forEach(e => e.remove());
  const meta = card.querySelector("[data-meta]");
  const body = card.querySelector("[data-body]");
  const runBtnEl = card.querySelector(".run-btn");
  const saveBtn = card.querySelector(".save-btn");
  body.className = "response-body";
  if (runBtnEl) runBtnEl.disabled = false;

  if (out.ok) {
    card.classList.add("ok");
    card.classList.remove("err");
    const costStr = out.cost ? `<span class="meta-cost" title="Estimated from public pricing">${fmtUsd(out.cost)}</span> · ` : "";
    meta.innerHTML = `<span style="color: var(--matrix);">●</span> ${costStr}${out.ms}ms`;
    body.innerHTML = renderMarkdown(out.text) || `<em style="color: var(--on-dark-3);">(empty)</em>`;
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.style.display = "";
      // Mount the multi-format dropdown menu (md/docx/pdf)
      mountExportMenu(
        saveBtn,
        ["md", "docx", "pdf"],
        () => {
          const md = buildResponseMarkdown(out);
          const provider = PROVIDERS[model.provider].label;
          const sections = [
            { heading: t("export.section.prompt"), markdown: state.lastPrompt },
            {
              heading: model.label,
              subhead: `${provider} · ${out.ms}ms · ${out.modelId}`,
              markdown: out.text,
            },
          ];
          return { title: `Lorenza · ${model.label}`, markdown: md, sections };
        },
        `lorenza-${slugify(model.label)}`
      );
    }
  } else {
    card.classList.add("err");
    card.classList.remove("ok");
    meta.innerHTML = `<span style="color: var(--coral);">●</span> ${out.ms}ms`;
    body.innerHTML = "";
    card.appendChild(buildErrorBox(out));
    if (saveBtn) saveBtn.style.display = "none";
  }
}

function setCardPending(card, model) {
  card.classList.add("pending");
  card.classList.remove("ok", "err");
  card.querySelectorAll(".response-error").forEach(e => e.remove());
  const meta = card.querySelector("[data-meta]");
  const body = card.querySelector("[data-body]");
  const runBtnEl = card.querySelector(".run-btn");
  const saveBtn = card.querySelector(".save-btn");
  meta.innerHTML = `<span style="color: var(--gold);">●</span> ${t("run.pending")}`;
  body.className = "response-body pending-body";
  body.innerHTML = `
    <div class="pending-message">${t("run.pending.msg")}</div>
    <button class="run-now-btn" data-run-now="${model.id}">
      <svg viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 2.5l9 5.5-9 5.5z"/></svg>
      ${t("run.this")}
    </button>
  `;
  body.querySelector(".run-now-btn").addEventListener("click", () => runSingle(model.id));
  if (runBtnEl) runBtnEl.disabled = false;
  if (saveBtn) saveBtn.style.display = "none";
}

function getOrCreateCard(model) {
  let card = responsesGrid.querySelector(`[data-card-model="${model.id}"]`);
  if (!card) {
    card = buildResponseCard(model);
    responsesGrid.appendChild(card);
    // Update grid columns count
    const total = responsesGrid.children.length;
    const cls = "cols-" + Math.min(total, 4);
    responsesGrid.className = "responses-grid " + cls;
  }
  return card;
}

function removeCardForModel(modelId) {
  const card = responsesGrid.querySelector(`[data-card-model="${modelId}"]`);
  if (card) card.remove();
  state.lastResponses = state.lastResponses.filter(r => r.modelId !== modelId);
  const okCount = state.lastResponses.filter(r => r.ok).length;
  if (okCount < 2) document.getElementById("synth-panel").style.display = "none";
  if (state.lastResponses.length === 0) {
    responsesPanel.style.display = "none";
  }
  // Update grid cols
  const total = responsesGrid.children.length;
  if (total > 0) {
    responsesGrid.className = "responses-grid cols-" + Math.min(total, 4);
  }
  document.getElementById("save-all-btn").style.display = okCount >= 1 ? "" : "none";
}

function refreshResponseSummary() {
  const ok = state.lastResponses.filter(r => r.ok).length;
  const total = state.lastResponses.length;
  responsesMeta.textContent = total > 0
    ? t("responses.summary.partial", { ok, total })
    : "";
  document.getElementById("save-all-btn").style.display = ok >= 1 ? "" : "none";
  if (ok >= 2) {
    document.getElementById("synth-panel").style.display = "";
  }
}

/* -------- runSingle: re-run / first-run a single model -------- */
export async function runSingle(modelId) {
  if (!state.lastPrompt) { toast(t("err.noprompt")); return; }
  const m = MODELS.find(x => x.id === modelId);
  if (!m) return;

  responsesPanel.style.display = "";
  const card = getOrCreateCard(m);
  setCardLoading(card);
  const constellation = getConstellation();
  if (constellation) constellation.setRunning([modelId]);

  // Scroll the card into view so the user sees the running response
  requestAnimationFrame(() => {
    card.scrollIntoView({ behavior: "smooth", block: "center" });
  });

  const start = performance.now();
  let out;
  try {
    const r = await callModel(m, state.lastPrompt, { attachments: getAttachments() });
    const cost = calcCost(modelId, r.inTokens, r.outTokens);
    addSpent(modelId, cost);
    const ms = Math.round(performance.now() - start);
    // Dual-write: runner-internal short names (ms, cost, inTokens, …) for
    // the response cards and exports, AND canonical names (durationMs,
    // costUsd, tokensIn, …) for session-io / history persistence. Removes
    // the field-mismatch bug where cloud saves dropped tokens + cost.
    out = {
      modelId, ok: true, text: r.text,
      inTokens: r.inTokens, outTokens: r.outTokens, cost, ms,
      tokensIn: r.inTokens, tokensOut: r.outTokens, costUsd: cost, durationMs: ms,
      provider: m.provider,
      finishReason: r.finishReason,
      truncated: !!r.truncated,
    };
    if (constellation) constellation.flashNode(modelId, true);
  } catch (err) {
    const ms = Math.round(performance.now() - start);
    const errMsg = err.message || String(err);
    out = {
      modelId, ok: false,
      err: errMsg, error: errMsg,
      provider: err.provider || PROVIDERS[m.provider].label,
      status: err.status || null,
      action: err.action || null,
      raw: err.raw || null,
      ms, durationMs: ms,
    };
    if (constellation) constellation.flashNode(modelId, false);
  }
  if (constellation) constellation.clearBeams();

  // Replace in lastResponses or add
  const idx = state.lastResponses.findIndex(r => r.modelId === modelId);
  if (idx >= 0) state.lastResponses[idx] = out;
  else state.lastResponses.push(out);

  applyCardResult(card, m, out);
  refreshResponseSummary();
}

export async function runQuery() {
  const prompt = promptInput.value.trim();
  if (!prompt) { toast(t("err.noprompt")); return; }
  if (state.selectedModels.size === 0) { toast(t("err.nomodels")); return; }

  state.lastPrompt = prompt;
  state.lastResponses = [];
  // A new fan-out starts a new "session arc" — wipe the per-session
  // syntheses / deliverables arrays so a saved snapshot reflects only
  // the current consolidation, not everything since page load.
  state.syntheses = [];
  state.deliverables = [];
  (state as any).lastInteractionId = '';
  responsesPanel.style.display = "";
  responsesMeta.textContent = "";

  // Scroll the responses panel into view so the user immediately sees results
  requestAnimationFrame(() => {
    responsesPanel.scrollIntoView({ behavior: "smooth", block: "start" });
  });

  const selected = MODELS.filter(m => state.selectedModels.has(m.id));
  const colsClass = "cols-" + Math.min(selected.length, 4);
  responsesGrid.className = "responses-grid " + colsClass;
  responsesGrid.innerHTML = "";

  const cards = {};
  selected.forEach(m => {
    const card = buildResponseCard(m);
    setCardLoading(card);
    responsesGrid.appendChild(card);
    cards[m.id] = card;
  });
  document.getElementById("save-all-btn").style.display = "none";

  runBtn.disabled = true;
  const tStart = performance.now();

  // Light up beams in the constellation for each running model
  const constellation = getConstellation();
  if (constellation) constellation.setRunning(selected.map(m => m.id));

  // Snapshot attachments once so every model in the fan-out sees the same
  // list (the user might add/remove while results are pending).
  const fanoutAttachments = getAttachments();
  const results = await Promise.allSettled(selected.map(async m => {
    const start = performance.now();
    try {
      const r = await callModel(m, prompt, { attachments: fanoutAttachments });
      const cost = calcCost(m.id, r.inTokens, r.outTokens);
      addSpent(m.id, cost);
      const ms = Math.round(performance.now() - start);
      const out = {
        modelId: m.id, ok: true, text: r.text,
        inTokens: r.inTokens, outTokens: r.outTokens, cost, ms,
        tokensIn: r.inTokens, tokensOut: r.outTokens, costUsd: cost, durationMs: ms,
        provider: m.provider,
        finishReason: r.finishReason,
        truncated: !!r.truncated,
      };
      if (constellation) constellation.flashNode(m.id, true);
      return out;
    } catch (err) {
      const ms = Math.round(performance.now() - start);
      const errMsg = err.message || String(err);
      const out = {
        modelId: m.id,
        ok: false,
        err: errMsg, error: errMsg,
        provider: err.provider || PROVIDERS[m.provider]?.label || m.provider,
        status: err.status || null,
        action: err.action || null,
        raw: err.raw || null,
        ms, durationMs: ms,
      };
      if (constellation) constellation.flashNode(m.id, false);
      return out;
    }
  }));

  if (constellation) constellation.clearBeams();

  let okCount = 0;
  results.forEach((res, i) => {
    const m = selected[i];
    const out = res.value;
    state.lastResponses.push(out);
    if (out.ok) okCount++;
    applyCardResult(cards[m.id], m, out);
    // Code-block harvest: each successful response gets scanned for fenced
    // blocks ≥60 chars and the harvest lands in state.deliverables so a
    // session save carries the code out as named files.
    if (out.ok && out.text) extractCodeBlocks(out.text, m.label);
  });

  const totalMs = Math.round(performance.now() - tStart);
  responsesMeta.textContent = t("responses.summary", { ok: okCount, total: selected.length, ms: totalMs });

  // Show "Save all" if at least one response succeeded
  document.getElementById("save-all-btn").style.display = okCount >= 1 ? "" : "none";

  if (okCount >= 2) {
    document.getElementById("synth-panel").style.display = "";
  }
  runBtn.disabled = false;
  updateRunButton();

  // Persist this fan-out to the user's history (best-effort, fire-and-forget).
  // The interaction id lives on state.lastInteractionId so synthesis +
  // exports can append to the SAME interaction instead of creating new ones.
  try {
    const selectedIds = selected.map(m => m.id);
    const payloadResponses = state.lastResponses.map((r: any) => ({
      model_id: r.modelId,
      provider: r.provider || (MODELS.find(m => m.id === r.modelId)?.provider) || null,
      text: r.text || null,
      ok: !!r.ok,
      error: r.error || null,
      duration_ms: typeof r.ms === 'number' ? r.ms : null,
      tokens_in:  typeof r.tokensIn === 'number'  ? r.tokensIn  : null,
      tokens_out: typeof r.tokensOut === 'number' ? r.tokensOut : null,
      cost_usd:   typeof r.costUsd === 'number'   ? r.costUsd   : null,
    }));
    createInteraction({
      prompt: state.lastPrompt || '',
      selected_models: selectedIds,
      language: getCurrentLang(),
      responses: payloadResponses,
      metadata: { total_ms: totalMs, ok_count: okCount },
    }).then(saved => {
      if (saved && saved.id) {
        (state as any).lastInteractionId = saved.id;
      }
    }).catch(() => { /* already logged by history-client */ });
  } catch (err) {
    console.warn('history persist failed (non-fatal):', err);
  }

  // Session autosave (no-op unless the user enabled either toggle in
  // settings). Fires after the fan-out is fully written to state so the
  // snapshot reflects the just-completed responses.
  triggerAutosave();
}

export function attachRunHandlers() {
  runBtn = document.getElementById("run-btn");
  promptInput = document.getElementById("prompt-input");
  charCount = document.getElementById("char-count");
  responsesPanel = document.getElementById("responses-panel");
  responsesGrid = document.getElementById("responses-grid");
  responsesMeta = document.getElementById("responses-meta");

  promptInput.addEventListener("input", () => {
    charCount.textContent = promptInput.value.length;
    updateRunButton();
  });
  promptInput.addEventListener("keydown", e => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (!runBtn.disabled) runBtn.click();
    }
  });

  runBtn.addEventListener("click", runQuery);

  /* -------- Prompt: Open from file & Save to file -------- */
  const promptFileInput = document.getElementById("prompt-file-input");
  const promptSourceLabel = document.getElementById("prompt-source");

  document.getElementById("prompt-open-btn").addEventListener("click", () => {
    promptFileInput.value = ""; // reset so re-selecting same file fires change
    promptFileInput.click();
  });

  promptFileInput.addEventListener("change", async e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Hard size limit: 1 MB plain text — protect the textarea
    if (file.size > 1024 * 1024) {
      toast(t("prompt.toobig"));
      return;
    }
    try {
      const text = await file.text();
      promptInput.value = text;
      charCount.textContent = text.length;
      promptSourceLabel.textContent = file.name;
      updateRunButton();
      promptInput.focus();
      promptInput.scrollIntoView({ behavior: "smooth", block: "center" });
      toast(t("prompt.loaded", { name: file.name }));
    } catch (err) {
      toast(t("prompt.loadfail") + ": " + (err.message || err));
    }
  });

  // Clear "loaded from" label when the user edits
  promptInput.addEventListener("input", () => {
    if (promptSourceLabel.textContent && promptSourceLabel.dataset.lastValue !== promptInput.value) {
      // small delay so initial load doesn't clear immediately
      promptSourceLabel.textContent = "";
    }
  });

  // Save prompt — multi-format dropdown (md / txt / docx / pdf)
  mountExportMenu(
    document.getElementById("prompt-save-btn"),
    ["md", "txt", "docx", "pdf"],
    () => {
      const text = promptInput.value.trim();
      if (!text) return null;
      const md = `# Lorenza · Prompt

**Date:** ${nowReadable()}
**Length:** ${text.length} chars

## Prompt

${text}
`;
      const sections = [
        { heading: t("prompt.title"), markdown: text },
      ];
      return {
        title: "Lorenza · Prompt",
        markdown: md,
        plainText: text,
        sections,
      };
    },
    "lorenza-prompt"
  );

  // Constellation toggle event from constellation.ts → runner-side: pending cards
  window.addEventListener("lp-constellation-toggle", (ev) => {
    const { modelId, wasSelected } = ev.detail;
    updateRunButton();
    // After a prompt has been run: track new selections in the responses panel
    if (state.lastPrompt) {
      if (wasSelected) {
        // Deselected — remove its card from the grid
        removeCardForModel(modelId);
      } else {
        // Newly selected — show pending card with "Run this" button
        const m = MODELS.find(x => x.id === modelId);
        if (m && !state.lastResponses.find(r => r.modelId === modelId)) {
          responsesPanel.style.display = "";
          const card = getOrCreateCard(m);
          setCardPending(card, m);
        }
      }
    }
  });

  // Selection-changed (× pill click) event → also possibly remove card
  window.addEventListener("lp-selection-changed", (ev) => {
    const { modelId } = ev.detail || {};
    updateRunButton();
    if (state.lastPrompt && modelId) removeCardForModel(modelId);
  });
}
